import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Box, Chip, Divider, Switch, Skeleton } from '@mui/material';

const ChatHistoryPage = () => {
    const { userId, astroId } = useParams();
    const location = useLocation();
    const token = localStorage.getItem("astroVani-admin-token");

    const [chatData, setChatData] = useState([]);
    const [userName, setUserName] = useState(location?.state?.userName || '');
    const [astroName, setAstroName] = useState(location?.state?.astroName || '');
    const [userLoading, setUserLoading] = useState(!location?.state?.userName);
    const [astroLoading, setAstroLoading] = useState(!location?.state?.astroName);
    const [chatDate, setChatDate] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);
    const isLoadingRef = useRef(false);
    const shouldMaintainScrollRef = useRef(false);
    const savedScrollPositionRef = useRef({ height: 0, top: 0 });
    const [isDisabled, setIsDisabled] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const isImage = (url) => {
        if (!url) return false;
        const cleanUrl = url.split("?")[0];
        return (
            /\.(jpeg|jpg|gif|png|webp|svg|heic|bmp)$/i.test(cleanUrl) ||
            url.startsWith("data:image/")
        );
    };

    const getDayLabel = (date) => {
        if (!date) return "";
        const messageDate = new Date(date);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        // Reset time to compare dates only
        const msgDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

        if (msgDateOnly.getTime() === todayOnly.getTime()) {
            return "Today";
        } else if (msgDateOnly.getTime() === tomorrowOnly.getTime()) {
            return "Tomorrow";
        } else if (msgDateOnly.getTime() === yesterdayOnly.getTime()) {
            return "Yesterday";
        } else {
            // Return day name or full date
            return messageDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        }
    };

    const getDateKey = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    };

    useEffect(() => {
        if (chatData?.length > 0) {
            setIsDisabled(chatData[0]?.isDisabled || false);
        }
    }, [chatData, userId, astroId]);

    useEffect(() => {
        if (userName) {
            setUserLoading(false);
            return;
        }

        const fetchUser = async () => {
            try {
                setUserLoading(true);
                let user = null;
                try {
                    // 1. Direct single-user API call (fast)
                    const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/user/${userId}`, {
                        headers: { Authorization: token },
                    });
                    user = res?.data?.data;
                } catch (singleErr) {
                    // 2. Fallback to list search if single-user endpoint is unavailable
                    const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/user`, {
                        headers: { Authorization: token },
                    });
                    user = res?.data?.data?.find(u => u._id === userId);
                }

                if (user) {
                    setUserName(user.fullName || user.name || user.email || 'User');
                } else {
                    setUserName('User');
                }
            } catch (err) {
                console.error("User fetch error:", err);
                setUserName('User');
            } finally {
                setUserLoading(false);
            }
        };

        if (userId) {
            fetchUser();
        } else {
            setUserLoading(false);
        }
    }, [userId, token, userName]);


    useEffect(() => {
        if (astroName) {
            setAstroLoading(false);
            return;
        }

        const fetchAstro = async () => {
            try {
                setAstroLoading(true);
                let astro = null;
                try {
                    // 1. Direct single-astrologer API call (fast)
                    const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/getAstro/${astroId}`, {
                        headers: { Authorization: token },
                    });
                    astro = res?.data?.data;
                } catch (singleErr) {
                    // 2. Fallback to list search if single-astrologer endpoint is unavailable
                    const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/getAstro`, {
                        headers: { Authorization: token },
                    });
                    astro = res?.data?.data?.find(a => a._id === astroId);
                }

                if (astro) {
                    setAstroName(astro.fullName || astro.name || astro.email || 'Astrologer');
                } else {
                    setAstroName('Astrologer');
                }
            } catch (err) {
                console.error("Astrologer fetch error:", err);
                setAstroName('Astrologer');
            } finally {
                setAstroLoading(false);
            }
        };

        if (astroId) {
            fetchAstro();
        } else {
            setAstroLoading(false);
        }
    }, [astroId, token, astroName]);


    const fetchChat = async (page = 1, append = false) => {
        if (!userId || !astroId || isLoadingRef.current) return;

        isLoadingRef.current = true;
        setLoading(true);

        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/chats/${userId}/${astroId}?page=${page}`, {
                headers: { Authorization: token },
            });

            // Updated to handle new API response structure with data.chats
            const messages = res?.data?.data || [];
            const pagination = res?.data?.pagination || {};

            if (Array.isArray(messages)) {
                if (append) {
                    // Prepend older messages to the beginning
                    setChatData(prev => [...messages, ...prev]);
                } else {
                    // Initial load or replace
                    setChatData(messages);
                    if (messages.length > 0) {
                        setChatDate(messages[0].timestamp);
                    }
                }

                setCurrentPage(pagination.currentPage || page);
                setTotalPages(pagination.totalPages || 1);
                setHasMore((pagination.currentPage || page) < (pagination.totalPages || 1));
            } else {
                console.error("API response for chats was not an array:", messages);
                if (!append) setChatData([]);
            }

        } catch (err) {
            console.error("Chat fetch error:", err);
            if (!append) setChatData([]);
        } finally {
            setLoading(false);
            isLoadingRef.current = false;
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        setTotalPages(1);
        setHasMore(true);
        setChatData([]);
        fetchChat(1, false);
    }, [userId, astroId, token]);

    const loadMoreMessages = useCallback(() => {
        if (!loading && hasMore && currentPage < totalPages) {
            const nextPage = currentPage + 1;
            const scrollContainer = scrollContainerRef.current;
            if (!scrollContainer) return;

            // Save current scroll position before loading
            savedScrollPositionRef.current = {
                height: scrollContainer.scrollHeight,
                top: scrollContainer.scrollTop
            };
            shouldMaintainScrollRef.current = true;

            fetchChat(nextPage, true);
        }
    }, [loading, hasMore, currentPage, totalPages]);

    // Scroll to bottom on initial load
    useEffect(() => {
        if (chatData?.length > 0 && currentPage === 1 && !shouldMaintainScrollRef.current) {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                setTimeout(() => {
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }, 100);
            }
        }
    }, [chatData?.length, currentPage]);

    // Maintain scroll position when loading more messages
    useEffect(() => {
        if (shouldMaintainScrollRef.current && chatData?.length > 0) {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                // Use multiple timeouts to ensure DOM is fully updated
                setTimeout(() => {
                    const newScrollHeight = scrollContainer.scrollHeight;
                    const heightDifference = newScrollHeight - savedScrollPositionRef.current.height;
                    scrollContainer.scrollTop = savedScrollPositionRef.current.top + heightDifference;
                    shouldMaintainScrollRef.current = false;
                }, 50);
            }
        }
    }, [chatData?.length]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            // Load more when scrolled to top (within 50px)
            if (scrollContainer.scrollTop <= 50 && hasMore && !loading && !isLoadingRef.current) {
                loadMoreMessages();
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [hasMore, loading, loadMoreMessages]);


    const formatDate = (dateStr) => {
        if (!dateStr) return "No Date";
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleDisableChat = async (currentValue) => {
        if (!userId || !astroId || !token) {
            console.error("Missing required parameters for disabling chat");
            return;
        }

        const newValue = !currentValue;

        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/chat/access/${userId}/${astroId}`,
                { isDisabled: newValue },
                {
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data) {
                setIsDisabled(newValue);
                // Optionally show a success message
                console.log('Chat access updated successfully');
                fetchChat(1, false);
            }
        } catch (error) {
            console.error("Error updating chat access:", error);
            // Optionally show an error message to the user
            // You can add toast notification here if needed
        }
    };

    return (
        <>
            {previewImage && (
                <div
                    onClick={() => setPreviewImage(null)}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.8)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                        cursor: "zoom-out",
                    }}
                >
                    <img
                        src={previewImage}
                        alt="preview"
                        style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 10 }}
                    />
                </div>
            )}
            <div className="container py-5 pt-1">
                <button
                    onClick={() => window.history.back()}
                    style={{
                        marginBottom: "10px",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "monospace",
                        fontSize: "18px",
                        color: "#007bff",
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        padding: "10px 20px",
                    }}
                >
                    <span style={{ marginRight: "8px" }}>&lt;</span>
                    <span style={{ position: "relative", display: "inline-block", textDecoration: "underline" }}>
                        Back
                        {/* <span
                        style={{
                            content: "''",
                            position: "absolute",
                            left: 0,
                            bottom: -2,
                            width: "100%",
                            height: "1px",
                            borderBottom: "2px solid #007bff",
                        }}
                    ></span> */}
                    </span>
                </button>

                <div className="card shadow-sm p-4">
                    <h2 className="text-center mb-4 fw-bold">Chat</h2>

                    <div className="d-flex align-items-center mb-3 justify-content-between" style={{ borderBottom: "1px solid #ccc", paddingBottom: "5px" }}>
                        {/* <img src="/user-icon.png" alt="user" style={{ width: 50, height: 50, marginRight: 10 }} /> */}
                        <div>
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                {userLoading ? (
                                    <Skeleton variant="rounded" width={110} height={20} animation="wave" sx={{ borderRadius: '4px' }} />
                                ) : (
                                    <strong>{userName || 'User'}</strong>
                                )}
                                <span className="text-muted fw-bold">-</span>
                                {astroLoading ? (
                                    <Skeleton variant="rounded" width={110} height={20} animation="wave" sx={{ borderRadius: '4px' }} />
                                ) : (
                                    <strong>{astroName || 'Astrologer'}</strong>
                                )}
                                {/* {(() => {
                                const totalAbusiveCount = (chatData || []).reduce((count, msg) => {
                                    const list = Array.isArray(msg?.abusiveWords)
                                        ? msg.abusiveWords.filter((w) => typeof w === 'string' && w.trim().length > 0)
                                        : typeof msg?.abusiveWords === 'string' && msg.abusiveWords.trim().length > 0
                                            ? [msg.abusiveWords.trim()]
                                            : [];
                                    return count + (list.length > 0 ? 1 : 0);
                                }, 0);
                                if (totalAbusiveCount === 0) return null;
                                return (
                                    <span
                                        className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 rounded-pill"
                                        style={{ fontSize: '11px', fontWeight: 600 }}
                                        title="Inappropriate or abusive words detected in this chat"
                                    >
                                        <i className="icofont-warning-alt me-1"></i>
                                        {totalAbusiveCount} Abusive {totalAbusiveCount === 1 ? 'Message' : 'Messages'}
                                    </span>
                                );
                            })()} */}
                            </div>
                            <small className="text-muted">Chat History</small>
                            <Box>
                                Do you want to disable the chat
                                <Switch
                                    checked={isDisabled}
                                    onChange={() => handleDisableChat(isDisabled)}
                                    color="warning"
                                />
                            </Box>
                        </div>
                        <div className="text-center text-muted">
                            {formatDate(chatDate)}
                        </div>
                    </div>



                    <div
                        ref={scrollContainerRef}
                        style={{ maxHeight: "400px", overflowY: "auto", padding: "0 10px" }}
                    >
                        {/* Loading indicator at top when loading more messages */}
                        {loading && chatData?.length > 0 && (
                            <div className="text-center text-muted p-2">
                                <small>Loading older messages...</small>
                            </div>
                        )}

                        {/* This rendering logic is now correct because chatData is guaranteed to be an array */}
                        {chatData?.length === 0 && !loading ? (
                            <div className="text-center text-muted p-3">No messages found for this chat.</div>
                        ) : (
                            (() => {
                                const messagesWithSeparators = [];
                                let lastDateKey = null;

                                chatData?.forEach((msg, index) => {
                                    const msgTimestamp = msg.timestamp || msg.timeStamp;
                                    const currentDateKey = getDateKey(msgTimestamp);

                                    if (currentDateKey !== lastDateKey && msgTimestamp) {
                                        const dayLabel = getDayLabel(msgTimestamp);
                                        messagesWithSeparators.push({
                                            type: "dateSeparator",
                                            date: msgTimestamp,
                                            label: dayLabel,
                                            key: `date-${currentDateKey}`
                                        });
                                        lastDateKey = currentDateKey;
                                    }

                                    messagesWithSeparators.push({
                                        ...msg,
                                        type: "message",
                                        uniqueId: msg._id || `msg-${index}`
                                    });
                                });

                                return messagesWithSeparators.map((item) => {
                                    if (item.type === "dateSeparator") {
                                        return (
                                            <div key={item.key} className="text-center my-3">
                                                <Divider
                                                    sx={{
                                                        '&::before, &::after': {
                                                            borderColor: '#007bff',
                                                            borderTop: '2px solid #007bff',
                                                        },
                                                    }}
                                                >
                                                    <Chip
                                                        label={item.label}
                                                        size="small"
                                                        sx={{
                                                            color: '#007bff',
                                                            borderColor: '#007bff',
                                                            backgroundColor: '#f0f7ff',
                                                            fontWeight: 600,
                                                            fontSize: '12px',
                                                            px: 1,
                                                        }}
                                                        variant="outlined"
                                                    />
                                                </Divider>
                                            </div>
                                        );
                                    }

                                    const msg = item;
                                    const isSenderUser = msg.senderId === userId;
                                    const isCurrentSenderLoading = isSenderUser ? userLoading : astroLoading;
                                    const senderName = isSenderUser ? (userName || 'User') : (astroName || 'Astrologer');

                                    // Detect image in attachment or text/message
                                    const attachmentIsImage = msg.attachment && isImage(msg.attachment);
                                    const textIsImage = (msg.text && isImage(msg.text)) || (msg.message && isImage(msg.message));

                                    const imageUrl = attachmentIsImage
                                        ? msg.attachment
                                        : textIsImage
                                            ? (msg.text || msg.message)
                                            : null;

                                    const rawText = (msg.text || msg.message || "").trim();
                                    const messageText = rawText === imageUrl ? "" : rawText;

                                    const shouldShowImage = !!imageUrl;
                                    const shouldShowText = !!messageText;

                                    // Extract and normalize abusive words
                                    const abusiveWordsList = Array.isArray(msg.abusiveWords)
                                        ? msg.abusiveWords.filter((w) => typeof w === 'string' && w.trim().length > 0)
                                        : typeof msg.abusiveWords === 'string' && msg.abusiveWords.trim().length > 0
                                            ? [msg.abusiveWords.trim()]
                                            : [];
                                    const hasAbusiveWords = abusiveWordsList.length > 0;

                                    return (
                                        <div key={msg.uniqueId} id={`msg-${msg._id}`} className="mb-3">
                                            <div className={`d-flex ${isSenderUser ? 'justify-content-start' : 'justify-content-end'}`}>
                                                <div
                                                    style={{
                                                        maxWidth: "70%",
                                                        background: hasAbusiveWords
                                                            ? (isSenderUser ? "#fff5f5" : "#fff1f1")
                                                            : (isSenderUser ? "#eef2f7" : "#d6f5d6"),
                                                        border: hasAbusiveWords ? "1.5px solid #f87171" : "none",
                                                        padding: "10px 15px",
                                                        borderRadius: "10px",
                                                        whiteSpace: "pre-wrap",
                                                        textAlign: "left",
                                                        position: "relative",
                                                        boxShadow: hasAbusiveWords ? "0 2px 6px rgba(239, 68, 68, 0.15)" : "none"
                                                    }}
                                                >
                                                    <div
                                                        className="d-flex align-items-center justify-content-between gap-2 mb-1"
                                                        style={{
                                                            fontWeight: 600,
                                                            color: hasAbusiveWords ? "#dc2626" : (isSenderUser ? "#1d67d6" : "#28a745")
                                                        }}
                                                    >
                                                        {isCurrentSenderLoading ? (
                                                            <Skeleton
                                                                variant="rounded"
                                                                width={75}
                                                                height={16}
                                                                animation="wave"
                                                                sx={{
                                                                    borderRadius: '4px',
                                                                    bgcolor: hasAbusiveWords
                                                                        ? 'rgba(239, 68, 68, 0.2)'
                                                                        : (isSenderUser ? 'rgba(29, 103, 214, 0.15)' : 'rgba(40, 167, 69, 0.15)')
                                                                }}
                                                            />
                                                        ) : (
                                                            <span>{senderName}</span>
                                                        )}
                                                        {hasAbusiveWords && (
                                                            <span
                                                                className="badge bg-danger text-white px-2 py-0.5 rounded-pill d-inline-flex align-items-center gap-1"
                                                                style={{ fontSize: "10px", fontWeight: 700 }}
                                                                title="Abusive words detected in this message"
                                                            >
                                                                <i className="icofont-warning-alt"></i> Abusive
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Reply To section */}
                                                    {msg.replyTo && (
                                                        <div
                                                            onClick={() => {
                                                                const el = document.getElementById(`msg-${msg.replyTo._id}`);
                                                                if (el) {
                                                                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                                                                    el.style.backgroundColor = "rgba(231, 178, 66, 0.3)";
                                                                    setTimeout(() => {
                                                                        el.style.backgroundColor = "transparent";
                                                                        el.style.transition = "background-color 0.5s ease";
                                                                    }, 1500);
                                                                }
                                                            }}
                                                            style={{
                                                                backgroundColor: "rgba(0,0,0,0.05)",
                                                                borderLeft: "4px solid #E7B242",
                                                                padding: "4px 8px",
                                                                marginBottom: "8px",
                                                                borderRadius: "6px",
                                                                cursor: "pointer",
                                                                fontSize: "0.85rem",
                                                                color: "#555",
                                                                overflow: "hidden"
                                                            }}
                                                        >
                                                            <span style={{ fontWeight: 600, color: "#E7B242", display: "block", marginBottom: "2px" }}>
                                                                {msg.replyTo.senderId === userId ? (userName || 'User') : (astroName || 'Astrologer')}
                                                            </span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                {msg.replyTo.attachment && (
                                                                    <img
                                                                        src={msg.replyTo.attachment}
                                                                        alt="replied-thumb"
                                                                        style={{ width: '30px', height: '30px', borderRadius: '4px', objectFit: 'cover' }}
                                                                    />
                                                                )}
                                                                <span style={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    display: "-webkit-box",
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: "vertical",
                                                                    wordBreak: "break-word"
                                                                }}>
                                                                    {msg.replyTo.text || (msg.replyTo.attachment ? "Attached an image" : "")}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Image content */}
                                                    {shouldShowImage && (
                                                        <div style={{ marginBottom: shouldShowText ? "8px" : "0" }}>
                                                            <img
                                                                src={imageUrl}
                                                                alt="Attachment"
                                                                style={{
                                                                    width: "200px",
                                                                    borderRadius: "8px",
                                                                    cursor: "pointer",
                                                                    display: "block"
                                                                }}
                                                                onClick={() => setPreviewImage(imageUrl)}
                                                                onError={(e) => { e.target.style.display = "none"; }}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Text content */}
                                                    {shouldShowText && (
                                                        <div style={{ wordBreak: "break-word" }}>{messageText}</div>
                                                    )}

                                                    {/* Abusive Words Detection Details */}
                                                    {hasAbusiveWords && (
                                                        <div
                                                            className="mt-2 p-2 rounded"
                                                            style={{
                                                                backgroundColor: "rgba(239, 68, 68, 0.08)",
                                                                border: "1px dashed #ef4444",
                                                                fontSize: "0.78rem",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center gap-1 text-danger mb-1" style={{ fontWeight: 700 }}>
                                                                <i className="icofont-exclamation-circle"></i>
                                                                <span>Abusive Words:</span>
                                                            </div>
                                                            <div className="d-flex flex-wrap gap-1 align-items-center">
                                                                {abusiveWordsList.map((word, wIdx) => (
                                                                    <span
                                                                        key={wIdx}
                                                                        className="badge bg-danger text-white px-2 py-1"
                                                                        style={{
                                                                            fontSize: "11px",
                                                                            fontWeight: 600,
                                                                            letterSpacing: "0.3px",
                                                                            borderRadius: "4px"
                                                                        }}
                                                                    >
                                                                        {word}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="text-muted small text-end mt-1">
                                                        {formatTime(msg.timestamp)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()
                        )}

                        {/* Show message when no more messages to load */}
                        {!hasMore && chatData?.length > 0 && (
                            <div className="text-center text-muted p-2">
                                <small>No more messages</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChatHistoryPage;